import * as React from 'react';
import {
  Button,
  MenuItem,
  MenuList,
  Popover,
  useTheme,
  Tooltip,
} from '@material-ui/core';
import { Avatar, Body, Heading, Icon } from 'copilot-design-system';

import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { Company } from 'src/constants';
import { useLogout } from 'src/hooks/useLogout';
import { PortalConfigContext } from 'src/context';
import { setActiveCompanyId } from 'src/store/activeCompany/slice';
import { useGetClientCompaniesQuery } from 'src/services/api/companiesApi';
import { useCompanySwitcherVisibility } from 'src/features/companies/useCompanySwitcherVisibility';

const useCompanySwitcher = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { SignoutRedirectURL: signoutRedirectURL } =
    React.useContext(PortalConfigContext);
  const companySwitcherVisibility = useCompanySwitcherVisibility();

  const activeClientCompanyId = useAppSelector(
    (state) => state.activeCompany.activeCompanyId,
  );
  const { data: companies = [], isLoading } = useGetClientCompaniesQuery();

  const handleLogout = useLogout();
  const handleClick = (company: Company) => () => {
    dispatch(setActiveCompanyId(company.id));
  };

  React.useEffect(() => {
    // if there are companies and no active company, set the first company as active
    // TODO: this is a temporary solution, we will discuss what should be the default company
    // either the last active company or the first company in the list.
    if (companies && companies.length > 0 && !activeClientCompanyId) {
      dispatch(setActiveCompanyId(companies.at(0)?.id ?? null));
    }
  }, [companies, activeClientCompanyId]);

  const selectedCompany = React.useMemo(() => {
    return companies?.find((company) => company.id === activeClientCompanyId);
  }, [companies, activeClientCompanyId]);

  return {
    companies,
    companySwitcherVisibility,
    handleClick,
    handleLogout,
    isLoading,
    theme,
    selectedCompany,
    signoutRedirectURL,
  } as const;
};

export const DesktopCompanySwitcher = () => {
  const { companies, companySwitcherVisibility, handleClick, selectedCompany } =
    useCompanySwitcher();

  if (companySwitcherVisibility.visibility !== 'desktop') {
    return null;
  }

  return (
    <div className="flex flex-col items-center gap-3 py-5">
      {companies.map((company) => (
        <Tooltip
          title={company.fields.name}
          key={company.id}
          placement="right"
          enterDelay={1000}
        >
          <button
            className="bg-transparent border-none shadow-none cursor-pointer"
            onClick={handleClick(company)}
          >
            <Avatar
              alt={`${company.fields.name} logo`}
              className={[
                selectedCompany?.id === company.id ? '' : 'border-transparent',
              ].join(' ')}
              fallbackColor={company.fields.fallbackColor}
              size="md"
              src={company.fields.avatarImageURL || ''}
              text={company.fields.name.charAt(0).toUpperCase()}
              variant="rounded"
            />
          </button>
        </Tooltip>
      ))}
    </div>
  );
};

export const MobileCompanySwitcher = () => {
  const {
    companies,
    companySwitcherVisibility,
    handleClick,
    handleLogout,
    theme,
    selectedCompany,
    signoutRedirectURL,
  } = useCompanySwitcher();
  const [open, setOpen] = React.useState(false);
  const anchorEl = React.useRef<HTMLButtonElement>(null);

  if (companySwitcherVisibility.visibility !== 'mobile') {
    return null;
  }

  return (
    <div>
      <Button
        className="bg-transparent border-none shadow-none"
        ref={anchorEl}
        onClick={() => setOpen(true)}
        style={{
          // TODO: remove this once we integrate the theme in tailwind
          backgroundColor: theme.palette.primary.dark,
        }}
      >
        {selectedCompany && (
          <>
            <Avatar
              className="mr-2 block sm:hidden"
              src={selectedCompany?.fields.avatarImageURL || ''}
              size="sm"
              fallbackColor={selectedCompany?.fields.fallbackColor}
              alt={selectedCompany?.fields.name || ''}
              variant="rounded"
              text={selectedCompany.fields.name.slice(0, 1)}
            />
            <Body className="text-white truncate" size="sm">
              {selectedCompany?.fields.name}
            </Body>
            <Icon
              icon={open ? 'ChevronUp' : 'ChevronDown'}
              className={'w-[12px] h-[12px] ml-[6px] text-white'}
            />
          </>
        )}
      </Button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        classes={{
          paper: 'w-[240px] mt-[2px] ml-[-8px] shadow-dropdown-menu',
        }}
      >
        <MenuList className="p-0">
          {companies.map((company) => (
            <MenuItem
              key={company.id}
              onClick={handleClick(company)}
              className="h-8 p-3"
            >
              <div className="flex items-center gap-2">
                <Avatar
                  src={company.fields.avatarImageURL || ''}
                  size="sm"
                  fallbackColor={company.fields.fallbackColor}
                  alt={company.fields.name}
                  variant="rounded"
                  text={company.fields.name.slice(0, 1)}
                />
                <Heading className="text-[14px]">{company.fields.name}</Heading>
              </div>
            </MenuItem>
          ))}
          <div className="h-[1px] bg-secondary-hover" />
          <MenuItem
            className="h-8 p-3"
            onClick={() => handleLogout(false, signoutRedirectURL, true)}
          >
            <Body className="text-[14px]">Log out</Body>
          </MenuItem>
        </MenuList>
      </Popover>
    </div>
  );
};
